import { action, observable, runInAction } from 'mobx';

import { BusyStore } from 'ui/base/button';
import { StringStore } from 'ui/base/input';

import { WeekdayStore } from 'ui/standard/input/weekday_input';
import { ClockInputStore } from 'ui/standard/input/clock_input';

import { PolicyInputStore } from 'ui/schedules/policy_input/policy_input_store';
import { DeviceInputStore } from 'ui/schedules/device_input/device_input_store';

import { Optional, ResultKind } from 'services/base/types';
import { IPangolinDomain, HostMap, Schedule } from 'services/pangolin/types';
import { ScheduleUpdateResult, SchedulesService } from 'services/pangolin/schedule_service';

import Messages from './edit_schedule_messages';

export class EditScheduleStore {
  private readonly schedule: Schedule;
  name: StringStore;
  start: ClockInputStore;
  end: ClockInputStore;
  activeDays: WeekdayStore;
  policies: PolicyInputStore;
  devices: DeviceInputStore;

  @observable.ref
  nameError?: string;

  @observable.ref
  daysError?: string;

  @observable.ref
  busy: BusyStore;

  constructor(schedule: Schedule, hosts: HostMap) {
    this.schedule = schedule;
    this.name = new StringStore(schedule.name);
    this.start = new ClockInputStore(schedule.start);
    this.end = new ClockInputStore(schedule.end);
    this.activeDays = new WeekdayStore(schedule.days);
    this.policies = new PolicyInputStore(schedule);
    this.devices = new DeviceInputStore(schedule, hosts);
    this.busy = new BusyStore();
  }

  @action.bound
  validate(schedule: Schedule): Optional<ScheduleUpdateResult> {
    let valid = true;

    if (schedule.name.trim().length === 0) {
      this.nameError = Messages.invalidName();
      valid = false;
    }

    const days = [
      schedule.days.monday,
      schedule.days.tuesday,
      schedule.days.wednesday,
      schedule.days.thursday,
      schedule.days.friday,
      schedule.days.saturday,
      schedule.days.sunday,
    ];

    if (!days.some((value) => value)) {
      this.daysError = Messages.invalidDays();
      valid = false;
    }

    return valid ? undefined : {
      kind: ResultKind.USER_ERROR,
      nameError: this.nameError,
      daysError: this.daysError,
    };
  }

  convertToSchedule(): Schedule {
    return {
      ...this.schedule,
      name: this.name.value,
      start: this.start.committedTime,
      end: this.end.committedTime,
      days: {
        ...this.activeDays.values,
      },
      hostIDs: this.devices.hostIDs,
      applyToAllHosts: this.devices.hasAllDevices,
      policies: this.policies.schedulePolicies,
      applyAllPolicies: this.policies.hasEverythingPolicy,
    };
  }

  @action.bound
  async handleSave(domain: IPangolinDomain): Promise<ScheduleUpdateResult> {
    this.nameError = undefined;
    this.daysError = undefined;

    const schedule = this.convertToSchedule();

    // Validate client-side
    const localResult = this.validate(schedule);
    if (localResult?.kind === ResultKind.USER_ERROR) {
      return localResult;
    }

    const result = await SchedulesService.update(domain, schedule);
    return runInAction(() => {
      if (result.kind === ResultKind.USER_ERROR) {
        this.nameError = result.nameError;
        this.daysError = result.daysError;
      }
      return result;
    });
  }
}
